import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatDateTime } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

// Jejak Audit — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
// Tabel ini yang PALING cepat tumbuh di sistem (±79 ribu baris di UAT, 21 Sep 2026):
// cari (tindakan / modul / email pelaku / jenis sasaran), urut, periode, dan jumlah
// dikerjakan server. Saringan bawaan: 30 hari terakhir — terlihat sebagai chip dan bisa
// dihapus untuk membaca seluruh riwayat. Cakupan peran (Owner semua; Kepala Cabang
// cabangnya + tindakannya sendiri) tetap ditentukan backend, bukan layar ini.

//: Cermin `URUT_SAH_AUDIT` di backend/routers/audit_logs.py.
const URUT_SAH = ["created_at", "module", "action", "actor_role_code"];

// K204 / VONIS-BUSINESS-66 (a): jejak audit yang lahir dari test di UAT tidak dihapus, tetapi DITANDAI
// (tabel penanda terpisah — baris audit sendiri tidak pernah diubah). Bawaan layar: tanpa jejak uji.
// Chip bisa dihapus untuk melihat semua catatan; baris asal uji diberi label "asal uji".
// Nilai cermin `jejak_uji` di backend/routers/audit_logs.py.
const JEJAK_UJI = { sembunyi: "Tanpa jejak uji", hanya: "Hanya jejak uji" };

const PERIODE = { "7": "7 hari terakhir", "30": "30 hari terakhir", "90": "90 hari terakhir", "365": "1 tahun terakhir" };

const KOLOM_CSV = [
  { kunci: "created_at", judul: "Waktu", ambil: (r) => formatDateTime(r.created_at) },
  { kunci: "actor_email", judul: "Pelaku", ambil: (r) => r.actor_email || "" },
  { kunci: "actor_role_code", judul: "Peran", ambil: (r) => r.actor_role_code || "" },
  { kunci: "module", judul: "Modul" },
  { kunci: "action", judul: "Tindakan" },
  { kunci: "target_type", judul: "Sasaran", ambil: (r) => r.target_type || "" },
  { kunci: "asal_uji", judul: "Asal uji", ambil: (r) => (r.asal_uji ? "ya" : "") },
];

export default function AuditLogs() {
  const { profile } = useAuth();
  const isOwner = profile?.role?.code === "OWNER";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [penandaAktif, setPenandaAktif] = useState(true);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      periode: { label: "Periode", param: "hari", bawaan: "30", tampil: (v) => PERIODE[v] || `${v} hari terakhir` },
      jejak: { label: "Jejak uji", param: "jejak_uji", bawaan: "sembunyi", tampil: (v) => JEJAK_UJI[v] || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    // Chip "Tanpa jejak uji" dihapus → minta SEMUA catatan secara eksplisit
    // (bawaan server = sembunyi, jadi parameter kosong tidak cukup).
    jejak_uji: paramSaringan.jejak_uji || "semua",
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/audit-logs", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.get("/audit-logs", { params: bangunParams() });
      setItems(res.data?.items || []);
      setTotal(typeof res.data?.total === "number" ? res.data.total : 0);
      setPenandaAktif(res.data?.penanda_uji_aktif !== false);
    } catch (e) {
      setErr(apiErrorMessage(e, "Jejak audit gagal dimuat."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const adaSaringan = daftar.cari.trim() || daftar.saringanAktif.length;

  return (
    <div className="space-y-5" data-testid="audit-logs-page">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Jejak Audit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Catatan tindakan penting. Isinya mengikuti peran dan cabang Anda.
          </p>
        </div>
        <Button data-testid="refresh-audit" variant="outline" onClick={load} disabled={loading}>
          {loading ? "Memuat..." : "Muat ulang"}
        </Button>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari tindakan / modul / email pelaku" testId="audit-palang"
                    ekspor={{ nama: "jejak_audit", kolom: KOLOM_CSV, ambilHalaman }}>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={daftar.nilaiSaringan.periode}
          onChange={(e) => daftar.setSaringan("periode", e.target.value)}
          aria-label="Saring periode"
          data-testid="audit-filter-periode"
        >
          <option value="">Semua waktu</option>
          {Object.entries(PERIODE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={daftar.nilaiSaringan.jejak}
          onChange={(e) => daftar.setSaringan("jejak", e.target.value)}
          aria-label="Saring jejak uji"
          data-testid="audit-filter-jejak-uji"
        >
          <option value="sembunyi">{JEJAK_UJI.sembunyi}</option>
          <option value="">Semua catatan</option>
          <option value="hanya">{JEJAK_UJI.hanya}</option>
        </select>
      </PalangDaftar>

      {!penandaAktif && (
        <div className="bg-muted border border-border p-3 rounded text-sm text-muted-foreground" data-testid="audit-penanda-belum-aktif">
          Penanda jejak uji belum aktif, jadi semua catatan ditampilkan apa adanya.
        </div>
      )}

      {err && (
        <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm text-danger-soft-foreground" data-testid="audit-galat">
          {err}
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-2">Waktu</ThUrut>
                <th className="px-4 py-2">Pelaku</th>
                <ThUrut daftar={daftar} kunci="actor_role_code" className="px-4 py-2">Peran</ThUrut>
                <ThUrut daftar={daftar} kunci="module" className="px-4 py-2">Modul</ThUrut>
                <ThUrut daftar={daftar} kunci="action" className="px-4 py-2">Tindakan</ThUrut>
                <th className="px-4 py-2">Sasaran</th>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-6 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-6 text-center text-muted-foreground" data-testid="audit-kosong">
                  {adaSaringan ? "Tidak ada catatan yang cocok dengan pencarian atau saringan ini." : "Belum ada catatan."}
                </td></tr>
              )}
              {daftar.tampil.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`audit-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                    {formatDateTime(r.created_at)}
                  </td>
                  {/* Identitas = actor_user_id (AUDIT-ACTOR-EMAIL-TEKS-BEBAS-01 bentuk d);
                      email kini distempel dari profil aktor oleh backend, tetapi
                      baris lama bisa memuat teks bebas — id ditampilkan di sampingnya. */}
                  <td className="px-4 py-2">
                    {r.actor_email || "—"}
                    {isOwner && r.actor_user_id && <span className="ml-1 text-xs text-muted-foreground font-mono" title={r.actor_user_id}>#{String(r.actor_user_id).slice(0, 8)}</span>}
                  </td>
                  <td className="px-4 py-2">{r.actor_role_code || "—"}</td>
                  <td className="px-4 py-2">{r.module}</td>
                  <td className="px-4 py-2 font-medium">
                    {r.action}
                    {r.asal_uji && (
                      <span className="ml-2 rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground"
                            title="Catatan ini lahir dari pengujian otomatis di masa uji coba, bukan tindakan staf sungguhan"
                            data-testid={`audit-asal-uji-${r.id}`}>asal uji</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{r.target_type || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="audit-paginasi" />
      </Card>
    </div>
  );
}
